import apiClient from "@/api/client";
import type { AuthUser } from "@/store/auth";

const wrap = <T>(res: { data: { data: T } }) => res.data.data;

export interface ManagedUser extends AuthUser {
  is_active: boolean;
}

export interface Branch {
  id: string;
  name: string;
  street?: string;
  city?: string;
  postal_code?: string;
  is_active: boolean;
}

export interface BranchFormData {
  name: string;
  street?: string;
  city?: string;
  postal_code?: string;
  is_active?: boolean;
}

export interface UserRolePatch {
  role?: AuthUser["role"];
  is_active?: boolean;
  is_ceo?: boolean;
  is_technical_director?: boolean;
  can_view_all_timesheets_and_mileage?: boolean;
  branch?: string | null;
  can_approve_orders?: boolean;
  can_view_tenders?: boolean;
  can_calculate_pricing?: boolean;
  can_approve_pricing?: boolean;
  can_issue_offers?: boolean;
  can_view_pricing_catalog?: boolean;
  can_use_estimating?: boolean;
  has_ze_advisor_access?: boolean;
  has_ze_tender_analytics_access?: boolean;
  can_view_inactive_in_schedule?: boolean;
  can_manage_contractors?: boolean;
  can_manage_agreement_groups?: boolean;
  can_confirm_led_titles?: boolean;
  can_create_projects?: boolean;
  can_submit_paper_docs_for_signing?: boolean;
  can_manage_doc_clusters?: boolean;
  can_view_correspondence_register?: boolean;
  is_finance_panel?: boolean;
  is_uz?: boolean;
  is_vat_payer?: boolean;
  vat_rate?: "zw" | "0" | "5" | "8" | "23" | null;
  has_small_account_access?: boolean;
  can_request_fees_any_project?: boolean;
  can_self_allocate?: boolean;
  is_non_project?: boolean;
  is_cross_branch_visible?: boolean;
  has_friday_access?: boolean;
  has_contacts_access?: boolean;
  contacts_branches?: string[];
  contacts_include_no_branch?: boolean;
  has_multisport_access?: boolean;
  has_browser_extensions_access?: boolean;
  can_use_ai_workshop?: boolean;
  can_use_aron_autopilot?: boolean;
  can_read_source_code?: boolean;
  has_chat_access?: boolean;
  has_two_drive_access?: boolean;
  has_lead_access?: boolean;
  has_eop_mail_access?: boolean;
  has_tech_calc_norms_access?: boolean;
  has_rfq_manage_access?: boolean;
  has_rfq_sealed_access?: boolean;
  has_rfq_view_access?: boolean;
  has_rfq_manage_permission?: boolean;
  has_rfq_sealed_permission?: boolean;
  has_rfq_view_permission?: boolean;
  can_access_rfq?: boolean;
  contract_type?: "uop" | "uz" | "b2b";
  job_title?: string;
}

export interface ProjectFinanceGrant {
  id: string;
  director: string;
  director_name: string;
  project: string;
  project_name: string;
  granted_by: string | null;
  granted_by_name: string | null;
  created_at: string;
}

export interface SalaryViewGrant {
  id: string;
  director: string;
  director_name: string;
  employee: string;
  employee_name: string;
  granted_by: string | null;
  granted_by_name: string | null;
  created_at: string;
}

export interface DirectorProjectSummary {
  director: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    role: string;
    is_ceo: boolean;
    profile_picture_url?: string | null;
  };
  project_grants: { grant_id: string; project_id: string; project_name: string }[];
}

export interface DirectorSalarySummary {
  director: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    role: string;
    is_ceo: boolean;
    profile_picture_url?: string | null;
    branch: { id: string; name: string } | null;
  };
  employee_grants: { grant_id: string; employee_id: string; employee_name: string }[];
}

export interface ScheduleViewGrant {
  id: string;
  viewer: string;
  viewer_name: string;
  employee: string;
  employee_name: string;
  granted_by: string | null;
  granted_by_name: string | null;
  created_at: string;
}

export interface ViewerScheduleSummary {
  viewer: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    role: string;
    is_ceo: boolean;
    profile_picture_url?: string | null;
    branch: { id: string; name: string } | null;
  };
  employee_grants: { grant_id: string; employee_id: string; employee_name: string }[];
}

export interface SchedulePickableUser {
  id: string;
  name: string;
  branch_id: string | null;
  branch_name: string | null;
}

export interface AgreementViewGrant {
  id: string;
  user: string;
  user_name: string;
  agreement: string;
  agreement_title: string;
  contractor_name: string;
  granted_by: string | null;
  granted_by_name: string | null;
  created_at: string;
}

export interface UserAgreementAccessSummary {
  user: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    role: string;
    is_ceo: boolean;
    profile_picture_url?: string | null;
  };
  agreement_grants: { grant_id: string; agreement_id: string; agreement_title: string; contractor_name: string }[];
}

export interface ConfidentialAgreementOption {
  id: string;
  title: string;
  contractor_name: string;
}

export interface AgreementPickableUser {
  id: string;
  name: string;
  role: string;
}

export interface GroupFinanceGrant {
  id: string;
  user: string;
  user_name: string;
  company: string;
  company_code: string;
  granted_by: string | null;
  granted_by_name: string | null;
  created_at: string;
}

export interface UserGroupFinanceSummary {
  user: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    role: string;
    is_ceo: boolean;
  };
  company_grants: { grant_id: string; company_id: string; company_code: string }[];
}

export type InternalCostLevel = "aggregate" | "full";

export interface InternalCostGrant {
  id: string;
  director: string;
  director_name: string;
  level: InternalCostLevel;
  granted_by: string | null;
  granted_by_name: string | null;
  created_at: string;
}

export interface DirectorInternalCostSummary {
  director: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    role: string;
    is_ceo: boolean;
  };
  grant: InternalCostGrant | null;
}

export interface DLCModuleAccessGrant {
  id: string;
  user: string;
  user_name: string;
  module: string;
  granted_by: string | null;
  granted_by_name: string | null;
  created_at: string;
}

export interface DLCModuleOption {
  value: string;
  label: string;
}

export interface UserDLCAccessSummary {
  user: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    role: string;
    is_ceo: boolean;
    profile_picture_url?: string | null;
    contractor_company_name?: string | null;
  };
  module_grants: { grant_id: string; module: string }[];
  /** Moduły przysługujące z urzędu (reguła roli, nie wpis do odznaczenia). */
  auto_modules?: string[];
}

export interface Company {
  id: string;
  short_code: string;
  name: string;
  is_active: boolean;
  sort_order: number;
  logo: { filename: string; url: string } | null;
  // Adres oddziału realizującego — drukowany na metryce formatki (#oddzial_*#).
  street: string;
  postal_code: string;
  city: string;
  phone: string;
  nip: string;
}

export interface CompanyFormData {
  short_code: string;
  name: string;
  is_active: boolean;
  sort_order: number;
  street: string;
  postal_code: string;
  city: string;
  phone: string;
  nip: string;
}

export const ceoApi = {
  // User role management
  listUsers: () =>
    apiClient.get<{ data: ManagedUser[] }>("/ceo/users/").then(wrap),

  patchUser: ({ id, data }: { id: string; data: UserRolePatch }) =>
    apiClient.patch<{ data: ManagedUser }>(`/ceo/users/${id}/`, data).then(wrap),


  // Project finance access
  listProjectGrants: () =>
    apiClient.get<{ data: ProjectFinanceGrant[] }>("/ceo/project-finance-access/").then(wrap),

  createProjectGrant: (data: { director: string; project: string }) =>
    apiClient.post<{ data: ProjectFinanceGrant }>("/ceo/project-finance-access/", data).then(wrap),

  deleteProjectGrant: (id: string) =>
    apiClient.delete(`/ceo/project-finance-access/${id}/`),

  listDirectorsWithProjectGrants: () =>
    apiClient.get<{ data: DirectorProjectSummary[] }>("/ceo/project-finance-access/directors/").then(wrap),

  // Salary view access
  listSalaryGrants: () =>
    apiClient.get<{ data: SalaryViewGrant[] }>("/ceo/salary-view-access/").then(wrap),

  createSalaryGrant: (data: { director: string; employee: string }) =>
    apiClient.post<{ data: SalaryViewGrant }>("/ceo/salary-view-access/", data).then(wrap),

  deleteSalaryGrant: (id: string) =>
    apiClient.delete(`/ceo/salary-view-access/${id}/`),

  listDirectorsWithSalaryGrants: () =>
    apiClient.get<{ data: DirectorSalarySummary[]; meta: { employees: { id: string; name: string; branch_id: string | null }[] } }>(
      "/ceo/salary-view-access/directors/"
    ).then((res) => ({ data: res.data.data, employees: res.data.meta.employees })),

  // Schedule view access
  createScheduleGrant: (data: { viewer: string; employee: string }) =>
    apiClient.post<{ data: ScheduleViewGrant }>("/ceo/schedule-view-access/", data).then(wrap),

  deleteScheduleGrant: (id: string) =>
    apiClient.delete(`/ceo/schedule-view-access/${id}/`),

  listViewersWithScheduleGrants: () =>
    apiClient.get<{ data: ViewerScheduleSummary[]; meta: { users: SchedulePickableUser[] } }>(
      "/ceo/schedule-view-access/viewers/"
    ).then((res) => ({ data: res.data.data, users: res.data.meta.users })),

  // Confidential agreement view access
  listUsersWithAgreementGrants: () =>
    apiClient
      .get<{ data: UserAgreementAccessSummary[]; meta: { users: AgreementPickableUser[]; agreements: ConfidentialAgreementOption[] } }>(
        "/ceo/agreement-view-access/users/"
      )
      .then((res) => ({ data: res.data.data, users: res.data.meta.users, agreements: res.data.meta.agreements })),

  createAgreementViewGrant: (data: { user: string; agreement: string }) =>
    apiClient.post<{ data: AgreementViewGrant }>("/ceo/agreement-view-access/", data).then(wrap),

  deleteAgreementViewGrant: (id: string) =>
    apiClient.delete(`/ceo/agreement-view-access/${id}/`),

  // Group finance access
  listUsersWithGroupGrants: () =>
    apiClient
      .get<{ data: UserGroupFinanceSummary[]; meta: { companies: { id: string; short_code: string; name: string }[] } }>(
        "/ceo/group-finance-access/directors/"
      )
      .then((res) => ({ data: res.data.data, companies: res.data.meta.companies })),

  createGroupFinanceGrant: (data: { user: string; company: string }) =>
    apiClient.post<{ data: GroupFinanceGrant }>("/ceo/group-finance-access/", data).then(wrap),

  deleteGroupFinanceGrant: (id: string) =>
    apiClient.delete(`/ceo/group-finance-access/${id}/`),

  // Internal cost access
  listDirectorsWithInternalCostAccess: () =>
    apiClient
      .get<{ data: DirectorInternalCostSummary[] }>("/ceo/internal-cost-access/directors/")
      .then(wrap),

  setInternalCostAccess: (data: { director: string; level: InternalCostLevel }) =>
    apiClient.post<{ data: InternalCostGrant }>("/ceo/internal-cost-access/", data).then(wrap),

  deleteInternalCostAccess: (id: string) =>
    apiClient.delete(`/ceo/internal-cost-access/${id}/`),

  // DLC module access (DLC_01 Projekty ZE, DLC_02 Obieg dokumentów)
  listUsersWithDLCAccess: () =>
    apiClient
      .get<{
        data: UserDLCAccessSummary[];
        meta: { modules: DLCModuleOption[]; contractor_modules?: string[] };
      }>("/ceo/dlc-access/users/")
      .then((res) => ({
        data: res.data.data,
        modules: res.data.meta.modules,
        // Moduły, które da się nadać podwykonawcy (DLC_01, DLC_02, DLC_12).
        contractorModules: res.data.meta.contractor_modules ?? [],
      })),

  createDLCAccessGrant: (data: { user: string; module: string }) =>
    apiClient.post<{ data: DLCModuleAccessGrant }>("/ceo/dlc-access/", data).then(wrap),

  deleteDLCAccessGrant: (id: string) =>
    apiClient.delete(`/ceo/dlc-access/${id}/`),

  // Company management
  listCompanies: () =>
    apiClient.get<{ data: Company[] }>("/ceo/companies/").then(wrap),

  createCompany: (data: CompanyFormData) =>
    apiClient.post<{ data: Company }>("/ceo/companies/", data).then(wrap),

  updateCompany: ({ id, data }: { id: string; data: Partial<CompanyFormData> }) =>
    apiClient.patch<{ data: Company }>(`/ceo/companies/${id}/`, data).then(wrap),

  deleteCompany: (id: string) =>
    apiClient.delete(`/ceo/companies/${id}/`),

  uploadCompanyLogo: ({ id, file }: { id: string; file: File }) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiClient
      .post<{ data: Company }>(`/ceo/companies/${id}/logo/`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(wrap);
  },

  deleteCompanyLogo: (id: string) =>
    apiClient.delete(`/ceo/companies/${id}/logo/`),

  // Branch management
  listBranches: () =>
    apiClient.get<{ data: Branch[] }>("/auth/branches/").then(wrap),

  createBranch: (data: BranchFormData) =>
    apiClient.post<{ data: Branch }>("/auth/branches/", data).then(wrap),

  updateBranch: ({ id, data }: { id: string; data: Partial<BranchFormData> }) =>
    apiClient.patch<{ data: Branch }>(`/auth/branches/${id}/`, data).then(wrap),
};
